import { useEffect, useMemo } from 'react';
import type { ReactNode } from 'react';
import { CssBaseline, ThemeProvider, createTheme, useMediaQuery } from '@mui/material';
import { useMyPreferences, useMe, useOperatorBranding } from '../api/queries';
import { ARMS_PRIMARY_HEX, ARMS_ACCENT_HEX } from './defaults';
import { parsePreferences } from './preferences';
import type { ThemeMode } from './preferences';

/**
 * Layered ARMS theme — precedence (highest wins):
 *   1. **User preference** — primaryHex / accentHex on auth.app_user.preferences
 *   2. **Operator branding** — operator_branding.primary_hex / accent_hex
 *   3. **ARMS defaults** — `ARMS_PRIMARY_HEX` / `ARMS_ACCENT_HEX`
 *
 * Both color slots cascade independently, so an operator can ship a
 * branded primary and the user can still override accent (or vice
 * versa). Setting either to empty / null in user prefs falls back
 * down the chain.
 *
 * Mode (light / dark / system) is per-user; system mode follows the
 * OS via prefers-color-scheme.
 */
export function ArmsThemeProvider({ children }: { children: ReactNode }) {
  const me = useMe();
  const prefsQ = useMyPreferences();
  const branding = useOperatorBranding(me.data?.operator_id ?? null);

  const prefs = useMemo(() => parsePreferences(prefsQ.data), [prefsQ.data]);
  const systemPrefersDark = useMediaQuery('(prefers-color-scheme: dark)');

  const mode = resolveMode(prefs.themeMode, systemPrefersDark);

  // user → operator → ARMS default. Same cascade for both slots.
  const primary = prefs.primaryHex
                ?? branding.data?.primary_hex
                ?? ARMS_PRIMARY_HEX;
  const accent  = prefs.accentHex
                ?? branding.data?.accent_hex
                ?? ARMS_ACCENT_HEX;

  const theme = useMemo(() => createTheme({
    palette: {
      mode,
      primary:   { main: primary },
      secondary: { main: accent  },
    },
    shape: { borderRadius: 8 },
    typography: {
      fontFamily: 'Inter, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
    },
    // Subtle background tint pulled from the primary so the operator's
    // brand permeates beyond the chrome — the user-set primary likewise
    // wins here because it's already been resolved above.
    components: {
      MuiAppBar: {
        styleOverrides: {
          colorInherit: { backgroundColor: mode === 'dark' ? undefined : '#fff' },
        },
      },
    },
  }), [mode, primary, accent]);

  // Reflect the resolved mode on <html data-theme="..."> so non-MUI CSS
  // (page background, scroll behaviour) can react.
  useEffect(() => {
    document.documentElement.dataset.theme = mode;
  }, [mode]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {children}
    </ThemeProvider>
  );
}

function resolveMode(pref: ThemeMode | undefined, systemPrefersDark: boolean): 'light' | 'dark' {
  if (pref === 'light' || pref === 'dark') return pref;
  // 'system' or unset → follow the OS
  return systemPrefersDark ? 'dark' : 'light';
}

/** True if `useMyPreferences` + `useOperatorBranding` haven't both resolved yet. Useful to avoid first-paint flicker. */
export function useIsThemeSettling(): boolean {
  const prefsQ = useMyPreferences();
  const me = useMe();
  const branding = useOperatorBranding(me.data?.operator_id ?? null);
  return prefsQ.isLoading || me.isLoading || branding.isLoading;
}
